'use client';

import { RestApiTipDto } from '@order-mage/integration-sdk';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';
import { useCartStore, getTipPresets, type CartTipSelection } from '@/entities/cart';
import { QueryApi } from '@/features/query/api';
import { useGetLocalizedValue } from '@/shared/lib/hooks/useGetLocalizedValue';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { roundTip } from '@/shared/lib/priceRound';
import { Country } from '@/shared/model/Country';
import { Currency } from '@/shared/model/Currency';
import { Form } from '@/shared/ui/form';
import { InputRow } from '@/shared/ui/input-row';
import { Media } from '@/shared/ui/media';
import { CartTipBoxAmountBox } from './cart-tip-box-amount-box';

type Props = {
    tip: RestApiTipDto;
};
export const CartTipBoxTipSection = ({ tip }: Props) => {
    const t = useTranslations();
    const getLocalizedValue = useGetLocalizedValue();
    const [selectedCurrency] = useSelectedCurrency();
    const formMethods = useFormContext();
    const subtotal = useCartStore(useShallow((s) => s.subtotal));
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const venueCountry = (venueQuery.data?.address?.country as Country) ?? Country.CZ;

    // One selection object per BE tip id — `type` says how `value` is to be read.
    const enabledFormName = `tips.${tip.id}.enabled`;
    const typeFormName = `tips.${tip.id}.type`;
    const valueFormName = `tips.${tip.id}.value`;
    const [isChecked, selectedType, selectedValue] = useWatch({
        control: formMethods.control,
        name: [enabledFormName, typeFormName, valueFormName],
    });

    const presets = useMemo(() => getTipPresets(tip, selectedCurrency as Currency), [tip, selectedCurrency]);

    const mainIcon = tip.icons?.[0];

    const selectPreset = (selection: CartTipSelection) =>
        formMethods.setValue(`tips.${tip.id}`, selection, { shouldDirty: true });

    const customValue = selectedType === 'custom' ? (selectedValue ?? '') : '';
    const customAmount = roundTip(
        Math.max(parseFloat(customValue) || 0, 0),
        selectedCurrency as Currency,
        venueCountry,
    );

    return (
        <div className="cart-tip-box-tip-section" data-testid="cart-tip-box-tip-section" data-tip-id={tip.id}>
            <InputRow
                as="div"
                isActive={isChecked}
                className="cart-tip-box-tip-section__input-row"
                data-testid="cart-tip-box-input-row"
                data-tip-id={tip.id}
            >
                <div className="cart-tip-box-tip-section__heading">
                    <label
                        className="cart-tip-box-tip-section__label"
                        data-testid="cart-tip-box-section-label"
                        data-tip-id={tip.id}
                    >
                        <Form.Checkbox
                            id={enabledFormName}
                            name={enabledFormName}
                            checked={!!isChecked}
                            data-testid="cart-tip-box-checkbox"
                            data-tip-id={tip.id}
                        />
                        {!!mainIcon?.fullUrl && (
                            <Media
                                data={{ url: mainIcon.fullUrl, alt: 'main-tip-icon' }}
                                className="cart-tip-box-tip-section__icon"
                                width={24}
                                height={24}
                                data-testid="cart-tip-box-section-icon"
                            />
                        )}
                        {getLocalizedValue(tip.name)}
                    </label>
                    {!!getLocalizedValue(tip.description ?? {}) && (
                        <span
                            className="cart-tip-box-tip-section__description"
                            data-testid="cart-tip-box-section-description"
                        >
                            {getLocalizedValue(tip.description ?? {})}
                        </span>
                    )}
                </div>
                {isChecked && (
                    <div
                        className="cart-tip-box-tip-section__amount-boxes"
                        data-testid="cart-tip-box-amount-boxes"
                        data-tip-id={tip.id}
                    >
                        {presets.map((preset) => {
                            // Preview the rounded tip (OMP-2166) so the button hint
                            // matches what actually gets applied and charged.
                            const presetValue = parseFloat(preset.value);
                            const tipAmount = roundTip(
                                preset.type === 'percentage' ? (subtotal * presetValue) / 100 : presetValue,
                                selectedCurrency as Currency,
                                venueCountry,
                            );

                            return (
                                <CartTipBoxAmountBox
                                    key={`${preset.type}-${preset.value}`}
                                    tipType={preset.type}
                                    value={preset.value}
                                    label={preset.label}
                                    icons={preset.icons}
                                    isActive={selectedType === preset.type && selectedValue === preset.value}
                                    amount={tipAmount}
                                    currency={selectedCurrency as Currency}
                                    onClick={() =>
                                        selectPreset({
                                            enabled: true,
                                            type: preset.type,
                                            value: preset.value,
                                        })
                                    }
                                    data-testid="cart-tip-box-amount-box"
                                    data-tip-id={tip.id}
                                    data-type={preset.type}
                                    data-value={preset.value}
                                />
                            );
                        })}
                        {tip.customEnabled && (
                            <CartTipBoxAmountBox
                                tipType="custom"
                                value={customValue}
                                label={tip.customLabel}
                                icons={tip.customIcons}
                                isActive={selectedType === 'custom'}
                                amount={customAmount}
                                currency={selectedCurrency as Currency}
                                onClick={() =>
                                    selectPreset({
                                        enabled: true,
                                        type: 'custom',
                                        value: customValue,
                                    })
                                }
                                data-testid="cart-tip-box-amount-box"
                                data-tip-id={tip.id}
                                data-type="custom"
                            />
                        )}
                    </div>
                )}
                {isChecked && selectedType === 'custom' && (
                    <Form.TextField
                        type="number"
                        name={valueFormName}
                        label={
                            getLocalizedValue(tip.customLabel ?? {}) || t('CART_TIP_BOX.CUSTOM_AMOUNT_LABEL')
                        }
                        fieldClassName="cart-tip-box-tip-section__custom-amount"
                        data-testid="cart-tip-box-custom-amount-input"
                        data-tip-id={tip.id}
                    />
                )}
            </InputRow>
        </div>
    );
};
